import { connectionOf } from "./heartbeat";
import { rearTrayLow } from "./rearTray";

// The backend sends each printer with paperLow and rearTrayLow, the counts its low
// paper mails go at, which admins set on the Settings page. An older backend does not,
// and had 100 and 5.
export const LOW_PAPER = 100;
const LOW_REAR_TRAY = 5;

const lowAt = (count, older) => (typeof count === "number" ? count : older);

// Which of the kiosk's papers are low: its A4, and the rear tray it sells photos from
export const lowPapersOf = (printer) => ({
  a4: (printer.paper || 0) <= lowAt(printer.paperLow, LOW_PAPER),
  rearTray: rearTrayLow(printer, lowAt(printer.rearTrayLow, LOW_REAR_TRAY)),
});

// A printer card shows one state, the one that needs attention first. The connection
// says whether the kiosk is reachable now, status "error" stays until an admin
// resolves it, a printer problem clears itself once a heartbeat reports the printer
// fine again, and status "disabled" takes the printer out of service.
export const HEALTH = {
  offline: {
    label: "Offline",
    rank: 0,
    badge: "text-red-700 bg-red-100 dark:bg-red-700 dark:text-red-100",
    dot: "bg-red-500",
  },
  error: {
    label: "Error",
    rank: 1,
    badge: "text-red-700 bg-red-100 dark:bg-red-700 dark:text-red-100",
    dot: "bg-red-500",
  },
  printerProblem: {
    label: "Printer problem",
    rank: 2,
    badge: "text-red-700 bg-red-100 dark:bg-red-700 dark:text-red-100",
    dot: "bg-red-500",
  },
  lowPaper: {
    label: "Low paper",
    rank: 3,
    badge: "text-yellow-700 bg-yellow-100 dark:bg-yellow-700 dark:text-yellow-100",
    dot: "bg-yellow-400",
  },
  lowInk: {
    label: "Low ink",
    rank: 4,
    badge: "text-yellow-700 bg-yellow-100 dark:bg-yellow-700 dark:text-yellow-100",
    dot: "bg-yellow-400",
  },
  never: {
    label: "No heartbeat",
    rank: 5,
    badge: "text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400",
    dot: "bg-gray-400",
  },
  ok: {
    label: "OK",
    rank: 6,
    badge: "text-green-700 bg-green-100 dark:bg-green-700 dark:text-green-100",
    dot: "bg-green-500",
  },
  disabled: {
    label: "Disabled",
    rank: 7,
    badge: "text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400",
    dot: "bg-gray-400",
  },
};

// The backend reads these from the printer status in the kiosk's last heartbeat
// (utils/heartbeat.js). It sends none while the kiosk is offline.
export const printerProblemsOf = (printer) => printer.printerErrors || [];

// A tank counts as low when it is at its lowest line or reaches it within these days
export const INK_LOW_DAYS = 3;

// The ink the backend estimates for one tank (GET /api/printer/ink or /api/service):
// null until someone has recorded it
export const inkLow = (tank) =>
  Boolean(tank) &&
  (tank.level <= 0 || (typeof tank.daysLeft === "number" && tank.daysLeft <= INK_LOW_DAYS));

// service: the kiosk's ink, { black, color }, once loaded
export const healthOf = (printer, service) => {
  if (printer.status === "disabled") return "disabled";
  const connection = connectionOf(printer);
  // Once a kiosk goes quiet, its status and paper count are from before it did.
  if (connection === "offline") return "offline";
  if (printer.status === "error") return "error";
  if (printerProblemsOf(printer).length > 0) return "printerProblem";
  const low = lowPapersOf(printer);
  if (low.a4 || low.rearTray) return "lowPaper";
  if (service && (inkLow(service.black) || inkLow(service.color))) return "lowInk";
  // A kiosk still on older code says nothing about its connection.
  if (connection === "never") return "never";
  return "ok";
};
